import {
  CargoSpace,
  CargoSpaceExtraFeatures,
  Load,
  LoadingSpaceType,
  WorkSpace,
  Group,
  LoadsCube, Axis,
} from '../../core/types';
import { Project } from '../../core/objects/project';
import { FormDataType } from '../../main-panel/add-load/add-load.component';
import { LoadGroupToAdd } from '../../core/services/import-export-service-types';

export function convertLoadingSpacesDimensions(
  loadingSpaces: LoadingSpaceType[],
  toMm: boolean,
): LoadingSpaceType[] {
  return loadingSpaces.map((loadingSpace: LoadingSpaceType) => {
    return convertLoadingSpaceDimensions(loadingSpace, toMm) as LoadingSpaceType;
  });
}

export function convertLoadingSpaceDimensions(
  loadingSpace: Partial<LoadingSpaceType>,
  toMm: boolean,
): Partial<LoadingSpaceType> {
  const multiplier: number = toMm ? 10 : 0.1;
  const convert = (value: number, zero: number = null) =>
    value != null ? (value === 0 ? zero : value * multiplier) : value;
  return {
    ...loadingSpace,
    axis: loadingSpace.axis ? loadingSpace.axis.map((axis: Axis) => ({
      ...axis,
      distanceFromSpaceFront: convert(axis.distanceFromSpaceFront, 0)
    })) : undefined,
    length: convert(loadingSpace.length),
    width: convert(loadingSpace.width),
    height: convert(loadingSpace.height),
    length1: convert(loadingSpace.length1),
    width1: convert(loadingSpace.width1),
    height1: convert(loadingSpace.height1),
    wheelTrailerWidth: convert(loadingSpace.wheelTrailerWidth),
    wheelTrailerHeight: convert(loadingSpace.wheelTrailerHeight),
    wheelTrailerLength: convert(loadingSpace.wheelTrailerLength),
    wheelTrailerDistance: convert(loadingSpace.wheelTrailerDistance),
    wheelSemiTrailerWidth: convert(loadingSpace.wheelSemiTrailerWidth),
    wheelSemiTrailerHeight: convert(loadingSpace.wheelSemiTrailerHeight),
    wheelSemiTrailerLength: convert(loadingSpace.wheelSemiTrailerLength),
    wheelSemiTrailerDistance: convert(loadingSpace.wheelSemiTrailerDistance),
    coolerTrailerWidth: convert(loadingSpace.coolerTrailerWidth),
    coolerTrailerHeight: convert(loadingSpace.coolerTrailerHeight),
    coolerTrailerLength: convert(loadingSpace.coolerTrailerLength),
    coolerTrailerFloorHeight: convert(loadingSpace.coolerTrailerFloorHeight),
    coolerSemiTrailerWidth: convert(loadingSpace.coolerSemiTrailerWidth),
    coolerSemiTrailerHeight: convert(loadingSpace.coolerSemiTrailerHeight),
    coolerSemiTrailerLength: convert(loadingSpace.coolerSemiTrailerLength),
    coolerSemiTrailerFloorHeight: convert(loadingSpace.coolerSemiTrailerFloorHeight),
    varioTrailerFloorHeight: convert(loadingSpace.varioTrailerFloorHeight),
    varioSemiTrailerFloorHeight: convert(loadingSpace.varioSemiTrailerFloorHeight),
    varioTrailerFloorThickness: convert(loadingSpace.varioTrailerFloorThickness),
    varioSemiTrailerFloorThickness: convert(loadingSpace.varioSemiTrailerFloorThickness),
    palletMaxHeight: convert(loadingSpace.palletMaxHeight),
    palletHeight: convert(loadingSpace.palletHeight),
    airLengthA: convert(loadingSpace.airLengthA),
    airWidth: convert(loadingSpace.airWidth),
    airBaseB: convert(loadingSpace.airBaseB),
    airHeightC: convert(loadingSpace.airHeightC),
    airHeightD: convert(loadingSpace.airHeightD),
    trailerDoorHeight: convert(loadingSpace.trailerDoorHeight),
    trailerDoorWidth: convert(loadingSpace.trailerDoorWidth),
    semiTrailerDoorHeight: convert(loadingSpace.semiTrailerDoorHeight),
    semiTrailerDoorWidth: convert(loadingSpace.semiTrailerDoorWidth),
    gravitySemiTrailerLengthTolerance: convert(loadingSpace.gravitySemiTrailerLengthTolerance),
    gravitySemiTrailerWidthTolerance: convert(loadingSpace.gravitySemiTrailerWidthTolerance),
    gravityTrailerLengthTolerance: convert(loadingSpace.gravityTrailerLengthTolerance),
    gravityTrailerWidthTolerance: convert(loadingSpace.gravityTrailerWidthTolerance),
  };
}

export function convertGroupToCm(groups: Group[]): Group[] {
  return groups.map((group: Group) => ({
    ...group,
    dimensions: group.dimensions
      ? {
          width: group.dimensions.width * 0.1,
          height: group.dimensions.height * 0.1,
          length: group.dimensions.length * 0.1,
          diameter: group.dimensions.diameter * 0.1,
        }
      : undefined,
    loadsCubes: group.loadsCubes.map((loadCube: LoadsCube) => ({
      ...loadCube,
      dimensions: loadCube.dimensions
        ? {
            width: loadCube.dimensions.width * 0.1,
            height: loadCube.dimensions.height * 0.1,
            length: loadCube.dimensions.length * 0.1,
            diameter: loadCube.dimensions.diameter * 0.1,
          }
        : undefined,
      palletContentGroups: loadCube.palletContentGroups
        ? convertGroupToCm(loadCube.palletContentGroups)
        : loadCube.palletContentGroups,
    })),
    palletContentGroups: group.palletContentGroups
      ? convertGroupToCm(group.palletContentGroups)
      : group.palletContentGroups,
  }));
}

export function convertToNewWorkspaceCm(workSpace: WorkSpace): WorkSpace {
  if (!workSpace || !workSpace.cargoSpaces) {
    return workSpace;
  }

  const newCargoSpaces: CargoSpace[] = workSpace.cargoSpaces.map(
    (cargoSpace: CargoSpace): CargoSpace => {
      if (!cargoSpace || !cargoSpace.dimensions) {
        return cargoSpace;
      }

      const newDimensions = {
        length: convertToCm(cargoSpace.dimensions.length),
        width: convertToCm(cargoSpace.dimensions.width),
        height: convertToCm(cargoSpace.dimensions.height),
      };

      const newGroups = cargoSpace.groups.map((group: Group): Group => {
        if (!group.dimensions) {
          return group;
        }

        const newGroupDimensions = {
          length: convertToCm(group.dimensions.length),
          width: convertToCm(group.dimensions.width),
          height: convertToCm(group.dimensions.height),
          diameter: convertToCm(group.dimensions.diameter),
        };

        const newLoadsCubes = group.loadsCubes.map((loadCube: LoadsCube): LoadsCube => {
          if (!loadCube.dimensions) {
            return loadCube;
          }

          const newLoadCubeDimensions = {
            length: convertToCm(loadCube.dimensions.length),
            width: convertToCm(loadCube.dimensions.width),
            height: convertToCm(loadCube.dimensions.height),
            diameter: convertToCm(loadCube.dimensions.diameter),
          };

          return {
            ...loadCube,
            dimensions: newLoadCubeDimensions,
          };
        });

        return {
          ...group,
          dimensions: newGroupDimensions,
          loadsCubes: newLoadsCubes,
        };
      });
      return {
        ...cargoSpace,
        dimensions: newDimensions,
        groups: newGroups,
      };
    },
  );

  return {
    ...workSpace,
    cargoSpaces: newCargoSpaces,
  };
}

function convertToCm(value: number): number {
  if (value === undefined || value === null) {
    return value;
  }
  return value * 0.1;
}

function convertCargoSpaceExtraFeatures(
  features: CargoSpaceExtraFeatures,
  convert: (value: number | undefined) => number | undefined,
): CargoSpaceExtraFeatures {
  return {
    ...features,
    wheelTrailerDistance: convert(features.wheelTrailerDistance),
    wheelSemiTrailerDistance: convert(features.wheelSemiTrailerDistance),
    wheelTrailerWidth: convert(features.wheelTrailerWidth),
    wheelTrailerHeight: convert(features.wheelTrailerHeight),
    wheelTrailerLength: convert(features.wheelTrailerLength),
    wheelSemiTrailerWidth: convert(features.wheelSemiTrailerWidth),
    wheelSemiTrailerHeight: convert(features.wheelSemiTrailerHeight),
    wheelSemiTrailerLength: convert(features.wheelSemiTrailerLength),
    coolerTrailerWidth: convert(features.coolerTrailerWidth),
    coolerTrailerHeight: convert(features.coolerTrailerHeight),
    coolerTrailerLength: convert(features.coolerTrailerLength),
    coolerTrailerFloorHeight: convert(features.coolerTrailerFloorHeight),
    coolerSemiTrailerFloorHeight: convert(features.coolerSemiTrailerFloorHeight),
    coolerSemiTrailerWidth: convert(features.coolerSemiTrailerWidth),
    coolerSemiTrailerHeight: convert(features.coolerSemiTrailerHeight),
    coolerSemiTrailerLength: convert(features.coolerSemiTrailerLength),
    trailerDoorHeight: convert(features.trailerDoorHeight),
    trailerDoorWidth: convert(features.trailerDoorWidth),
    semiTrailerDoorHeight: convert(features.semiTrailerDoorHeight),
    semiTrailerDoorWidth: convert(features.semiTrailerDoorWidth),
    palletHeight: convert(features.palletHeight),
    palletMaxHeight: convert(features.palletMaxHeight),
    airLengthA: convert(features.airLengthA),
    airWidth: convert(features.airWidth),
    airBaseB: convert(features.airBaseB),
    airHeightC: convert(features.airHeightC),
    airHeightD: convert(features.airHeightD),
    varioTrailerFloorHeight: convert(features.varioTrailerFloorHeight),
    varioSemiTrailerFloorHeight: convert(features.varioSemiTrailerFloorHeight),
    varioTrailerFloorThickness: convert(features.varioTrailerFloorThickness),
    varioSemiTrailerFloorThickness: convert(features.varioSemiTrailerFloorThickness),
    trailerCOGX: convert(features.trailerCOGX),
    trailerCOGY: convert(features.trailerCOGY),
    trailerCOGZ: convert(features.trailerCOGZ),
    semiTrailerCOGX: convert(features.semiTrailerCOGX),
    semiTrailerCOGY: convert(features.semiTrailerCOGY),
    semiTrailerCOGZ: convert(features.semiTrailerCOGZ),
    gravityTrailerWidthTolerance: convert(features.gravityTrailerWidthTolerance),
    gravityTrailerLengthTolerance: convert(features.gravityTrailerLengthTolerance),
    gravitySemiTrailerWidthTolerance: convert(features.gravitySemiTrailerWidthTolerance),
    gravitySemiTrailerLengthTolerance: convert(features.gravitySemiTrailerLengthTolerance),
  };
}

export function convertProjectDimensions(project: Project, toMm: boolean): Project {
  const multiplier: number = toMm ? 10 : 0.1;
  const convert = (value: number | undefined): number | undefined =>
    value !== undefined ? parseFloat((value * multiplier).toFixed(2)) : undefined;

  return {
    ...project,
    workSpaces: project.workSpaces.map((workSpace: WorkSpace) => ({
      ...workSpace,
      axis: workSpace.axis ? workSpace.axis.map(axis => ({
        ...axis,
        distanceFromSpaceFront: convert(axis.distanceFromSpaceFront)
      })) : undefined,
      cargoSpaces: workSpace.cargoSpaces.map((cargoSpace: CargoSpace) => ({
        ...cargoSpace,
        axis: cargoSpace.axis ? cargoSpace.axis.map(axis => ({
          ...axis,
          distanceFromSpaceFront: convert(axis.distanceFromSpaceFront)
        })) : undefined,
        dimensions: cargoSpace.dimensions
          ? {
              ...cargoSpace.dimensions,
              width: convert(cargoSpace.dimensions.width),
              height: convert(cargoSpace.dimensions.height),
              length: convert(cargoSpace.dimensions.length),
            }
          : undefined,
        extraFeatures: cargoSpace.extraFeatures
          ? convertCargoSpaceExtraFeatures(cargoSpace.extraFeatures, convert)
          : undefined,
        groups: cargoSpace.groups.map((group: Group) => ({
          ...group,
          dimensions: group.dimensions
            ? {
                ...group.dimensions,
                width: convert(group.dimensions.width),
                height: convert(group.dimensions.height),
                length: convert(group.dimensions.length),
                diameter: convert(group.dimensions.diameter),
              }
            : undefined,
          loadsCubes: group.loadsCubes.map((loadCube: LoadsCube) => ({
            ...loadCube,
            dimensions: loadCube.dimensions
              ? {
                  ...loadCube.dimensions,
                  width: convert(loadCube.dimensions.width),
                  height: convert(loadCube.dimensions.height),
                  length: convert(loadCube.dimensions.length),
                  diameter: convert(loadCube.dimensions.diameter),
                }
              : undefined,
          })),
        })),
      })),
      outsideSpace: {
        ...workSpace.outsideSpace,
        dimensions: workSpace.outsideSpace.dimensions
          ? {
              ...workSpace.outsideSpace.dimensions,
              width: convert(workSpace.outsideSpace.dimensions.width),
              height: convert(workSpace.outsideSpace.dimensions.height),
              length: convert(workSpace.outsideSpace.dimensions.length),
            }
          : undefined,
        groups: workSpace.outsideSpace.groups.map((group: Group) => ({
          ...group,
          dimensions: group.dimensions
            ? {
                ...group.dimensions,
                width: convert(group.dimensions.width),
                height: convert(group.dimensions.height),
                length: convert(group.dimensions.length),
                diameter: convert(group.dimensions.diameter),
              }
            : undefined,
          loadsCubes: group.loadsCubes.map((loadCube: LoadsCube) => ({
            ...loadCube,
            dimensions: loadCube.dimensions
              ? {
                  ...loadCube.dimensions,
                  width: convert(loadCube.dimensions.width),
                  height: convert(loadCube.dimensions.height),
                  length: convert(loadCube.dimensions.length),
                  diameter: convert(loadCube.dimensions.diameter),
                }
              : undefined,
          })),
        })),
      },
      unloadingSpace: {
        ...workSpace.unloadingSpace,
        dimensions: workSpace.unloadingSpace.dimensions
          ? {
              ...workSpace.unloadingSpace.dimensions,
              width: convert(workSpace.unloadingSpace.dimensions.width),
              height: convert(workSpace.unloadingSpace.dimensions.height),
              length: convert(workSpace.unloadingSpace.dimensions.length),
            }
          : undefined,
        groups: workSpace.unloadingSpace.groups.map((group: Group) => ({
          ...group,
          dimensions: group.dimensions
            ? {
                ...group.dimensions,
                width: convert(group.dimensions.width),
                height: convert(group.dimensions.height),
                length: convert(group.dimensions.length),
                diameter: convert(group.dimensions.diameter),
              }
            : undefined,
          loadsCubes: group.loadsCubes.map((loadCube: LoadsCube) => ({
            ...loadCube,
            dimensions: loadCube.dimensions
              ? {
                  ...loadCube.dimensions,
                  width: convert(loadCube.dimensions.width),
                  height: convert(loadCube.dimensions.height),
                  length: convert(loadCube.dimensions.length),
                  diameter: convert(loadCube.dimensions.diameter),
                }
              : undefined,
          })),
        })),
      },
      stops: workSpace.stops.map((stop: WorkSpace) => ({
        ...stop,
        axis: stop.axis ? stop.axis.map(axis => ({
          ...axis,
          distanceFromSpaceFront: convert(axis.distanceFromSpaceFront)
        })) : undefined,
        cargoSpaces: stop.cargoSpaces.map((cargoSpace: CargoSpace) => ({
          ...cargoSpace,
          axis: cargoSpace.axis ? cargoSpace.axis.map(axis => ({
            ...axis,
            distanceFromSpaceFront: convert(axis.distanceFromSpaceFront)
          })) : undefined,
          dimensions: cargoSpace.dimensions
            ? {
                ...cargoSpace.dimensions,
                width: convert(cargoSpace.dimensions.width),
                height: convert(cargoSpace.dimensions.height),
                length: convert(cargoSpace.dimensions.length),
              }
            : undefined,
          extraFeatures: cargoSpace.extraFeatures
            ? convertCargoSpaceExtraFeatures(cargoSpace.extraFeatures, convert)
            : undefined,
          groups: cargoSpace.groups.map((group: Group) => ({
            ...group,
            dimensions: group.dimensions
              ? {
                  ...group.dimensions,
                  width: convert(group.dimensions.width),
                  height: convert(group.dimensions.height),
                  length: convert(group.dimensions.length),
                  diameter: convert(group.dimensions.diameter),
                }
              : undefined,
            loadsCubes: group.loadsCubes.map((loadCube: LoadsCube) => ({
              ...loadCube,
              dimensions: loadCube.dimensions
                ? {
                    ...loadCube.dimensions,
                    width: convert(loadCube.dimensions.width),
                    height: convert(loadCube.dimensions.height),
                    length: convert(loadCube.dimensions.length),
                    diameter: convert(loadCube.dimensions.diameter),
                  }
                : undefined,
            })),
          })),
        })),
        outsideSpace: {
          ...stop.outsideSpace,
          dimensions: stop.outsideSpace.dimensions
            ? {
                ...stop.outsideSpace.dimensions,
                width: convert(stop.outsideSpace.dimensions.width),
                height: convert(stop.outsideSpace.dimensions.height),
                length: convert(stop.outsideSpace.dimensions.length),
              }
            : undefined,
          groups: stop.outsideSpace.groups.map((group: Group) => ({
            ...group,
            dimensions: group.dimensions
              ? {
                  ...group.dimensions,
                  width: convert(group.dimensions.width),
                  height: convert(group.dimensions.height),
                  length: convert(group.dimensions.length),
                  diameter: convert(group.dimensions.diameter),
                }
              : undefined,
            loadsCubes: group.loadsCubes.map((loadCube: LoadsCube) => ({
              ...loadCube,
              dimensions: loadCube.dimensions
                ? {
                    ...loadCube.dimensions,
                    width: convert(loadCube.dimensions.width),
                    height: convert(loadCube.dimensions.height),
                    length: convert(loadCube.dimensions.length),
                    diameter: convert(loadCube.dimensions.diameter),
                  }
                : undefined,
            })),
          })),
        },
        unloadingSpace: {
          ...stop.unloadingSpace,
          dimensions: stop.unloadingSpace.dimensions
            ? {
                ...stop.unloadingSpace.dimensions,
                width: convert(stop.unloadingSpace.dimensions.width),
                height: convert(stop.unloadingSpace.dimensions.height),
                length: convert(stop.unloadingSpace.dimensions.length),
              }
            : undefined,
          groups: stop.unloadingSpace.groups.map((group: Group) => ({
            ...group,
            dimensions: group.dimensions
              ? {
                  ...group.dimensions,
                  width: convert(group.dimensions.width),
                  height: convert(group.dimensions.height),
                  length: convert(group.dimensions.length),
                  diameter: convert(group.dimensions.diameter),
                }
              : undefined,
            loadsCubes: group.loadsCubes.map((loadCube: LoadsCube) => ({
              ...loadCube,
              dimensions: loadCube.dimensions
                ? {
                    ...loadCube.dimensions,
                    width: convert(loadCube.dimensions.width),
                    height: convert(loadCube.dimensions.height),
                    length: convert(loadCube.dimensions.length),
                    diameter: convert(loadCube.dimensions.diameter),
                  }
                : undefined,
            })),
          })),
        },
      })),
    })),
  };
}

export function convertSaveLoadingSpaceToCm(
  loadingSpace: Partial<LoadingSpaceType>,
): Partial<LoadingSpaceType> {
  const multiplier = 0.1;
  const convert = (value: number | undefined): number => (value ? value * multiplier : undefined);
  return {
    ...loadingSpace,
    length: convert(loadingSpace.length),
    width: convert(loadingSpace.width),
    height: convert(loadingSpace.height),
    unit: undefined,
    palletHeight: convert(loadingSpace.palletHeight),
    palletMaxHeight: convert(loadingSpace.palletMaxHeight),
    airLengthA: convert(loadingSpace.airLengthA),
    airWidth: convert(loadingSpace.airWidth),
    airBaseB: convert(loadingSpace.airBaseB),
    airHeightC: convert(loadingSpace.airHeightC),
    airHeightD: convert(loadingSpace.airHeightD),
  };
}

export function convertLoadsDimensions(loads: Load[], isMm: boolean): Load[] {
  return loads.map((load: Load) => {
    const multiplier = isMm ? 10 : 0.1;
    return convertLoad(load, multiplier) as Load;
  });
}

export function convertCsvLoadsDimensions(
  groupToAdd: LoadGroupToAdd,
  isMm: boolean,
): LoadGroupToAdd {
  const convertedNewLoads = groupToAdd.newLoads.map((newLoad) => {
    return {
      ...newLoad,
      load: convertLoad(newLoad.load as Load, isMm ? 10 : 0.1) as Load,
    };
  });
  const returnGroupToAdd = {
    ...groupToAdd,
    newLoads: convertedNewLoads,
  };
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return returnGroupToAdd as LoadGroupToAdd;
}

function convertLoad(load: FormDataType | Load, multiplier: number): FormDataType | Load {
  if (load === undefined) {
    return undefined;
  }
  return {
    ...load,
    width: load.width !== undefined ? load.width * multiplier : load.width,
    height: load.height !== undefined ? load.height * multiplier : load.height,
    length: load.length !== undefined ? load.length * multiplier : load.length,
    diameter: load.diameter !== undefined ? load.diameter * multiplier : load.diameter,
  };
}

export function convertLoadDimensions(loadData: FormDataType, unit: string): FormDataType {
  const isMm: boolean = unit === 'mm';
  const multiplier = isMm ? 0.1 : 1;
  return convertLoad(loadData, multiplier) as FormDataType;
}
